import React, { useState } from 'react';
import { Shield, Lock, User, Mail, Key, LogOut, Save, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { auth } from '../api/client';
import { useNavigate } from 'react-router-dom';

const SecuritySettings = () => {
  const navigate = useNavigate();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 3600,
    loginNotifications: true,
    ipWhitelist: false,
    whitelistedIPs: [],
    apiKeyRotation: 'monthly',
    auditLogging: true,
  });

  const [newIP, setNewIP] = useState('');

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setSaving(true);
    try {
      // TODO: Appeler API pour changer le mot de passe
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Mot de passe changé avec succès');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert('Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      localStorage.removeItem('access_token');
      navigate('/login');
    }
  };

  const addIP = () => {
    if (newIP && !securitySettings.whitelistedIPs.includes(newIP)) {
      setSecuritySettings({
        ...securitySettings,
        whitelistedIPs: [...securitySettings.whitelistedIPs, newIP]
      });
      setNewIP('');
    }
  };

  const removeIP = (ip) => {
    setSecuritySettings({
      ...securitySettings,
      whitelistedIPs: securitySettings.whitelistedIPs.filter(i => i !== ip)
    });
  };

  const saveSecuritySettings = async () => {
    setSaving(true);
    try {
      // TODO: Appeler API pour sauvegarder
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Paramètres de sécurité sauvegardés');
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-6 h-6 text-brand-green" />
          <h3 className="text-xl font-bold text-white">Changer le Mot de Passe</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Mot de passe actuel
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-brand-green"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-brand-green"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères, incluez majuscules, chiffres et symboles</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Confirmer le nouveau mot de passe
            </label>
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              placeholder="••••••••"
            />
          </div>

          <button
            onClick={handleChangePassword}
            disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            className="px-6 py-2 bg-brand-green text-brand-black font-semibold rounded-lg hover:bg-brand-green-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Changement...' : 'Changer le Mot de Passe'}
          </button>
        </div>
      </div>

      {/* Security Options */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-brand-green" />
            <h3 className="text-xl font-bold text-white">Paramètres de Sécurité</h3>
          </div>
          <button
            onClick={saveSecuritySettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Sauvegarder
          </button>
        </div>

        <div className="space-y-4">
          {/* Two-Factor Authentication */}
          <label className="flex items-center justify-between p-4 bg-brand-black-lighter rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-brand-green" />
              <div>
                <p className="text-white font-medium">Authentification à deux facteurs (2FA)</p>
                <p className="text-sm text-gray-400">Protection supplémentaire pour votre compte</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.twoFactorAuth}
              onChange={(e) => setSecuritySettings({...securitySettings, twoFactorAuth: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
          </label>

          {/* Session Timeout */}
          <div className="p-4 bg-brand-black-lighter rounded-lg border border-gray-700">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Expiration de session (secondes)
            </label>
            <select
              value={securitySettings.sessionTimeout}
              onChange={(e) => setSecuritySettings({...securitySettings, sessionTimeout: parseInt(e.target.value)})}
              className="w-full bg-brand-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              <option value="1800">30 minutes</option>
              <option value="3600">1 heure</option>
              <option value="7200">2 heures</option>
              <option value="14400">4 heures</option>
              <option value="28800">8 heures</option>
              <option value="86400">24 heures</option>
            </select>
          </div>

          {/* Login Notifications */}
          <label className="flex items-center justify-between p-4 bg-brand-black-lighter rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-brand-green" />
              <div>
                <p className="text-white font-medium">Notifications de connexion</p>
                <p className="text-sm text-gray-400">Recevoir un email à chaque connexion</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.loginNotifications}
              onChange={(e) => setSecuritySettings({...securitySettings, loginNotifications: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
          </label>

          {/* IP Whitelist */}
          <div className="p-4 bg-brand-black-lighter rounded-lg border border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-brand-green" />
                <div>
                  <p className="text-white font-medium">Whitelist IP</p>
                  <p className="text-sm text-gray-400">Restreindre l'accès à certaines IP</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={securitySettings.ipWhitelist}
                onChange={(e) => setSecuritySettings({...securitySettings, ipWhitelist: e.target.checked})}
                className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
              />
            </div>

            {securitySettings.ipWhitelist && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newIP}
                    onChange={(e) => setNewIP(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addIP()}
                    placeholder="192.168.1.0/24"
                    className="flex-1 bg-brand-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
                  />
                  <button
                    onClick={addIP}
                    className="px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
                
                <div className="space-y-1">
                  {securitySettings.whitelistedIPs.map((ip, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-brand-black rounded px-3 py-2">
                      <span className="text-white text-sm">{ip}</span>
                      <button
                        onClick={() => removeIP(ip)}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  {securitySettings.whitelistedIPs.length === 0 && (
                    <p className="text-xs text-gray-500 italic">Aucune IP whitelistée</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* API Key Rotation */}
          <div className="p-4 bg-brand-black-lighter rounded-lg border border-gray-700">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Rotation automatique des clés API
            </label>
            <select
              value={securitySettings.apiKeyRotation}
              onChange={(e) => setSecuritySettings({...securitySettings, apiKeyRotation: e.target.value})}
              className="w-full bg-brand-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              <option value="weekly">Hebdomadaire</option>
              <option value="monthly">Mensuel (Recommandé)</option>
              <option value="quarterly">Trimestriel</option>
              <option value="never">Jamais (Non recommandé)</option>
            </select>
          </div>

          {/* Audit Logging */}
          <label className="flex items-center justify-between p-4 bg-brand-black-lighter rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-brand-green" />
              <div>
                <p className="text-white font-medium">Audit Logging</p>
                <p className="text-sm text-gray-400">Enregistrer toutes les actions utilisateur</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.auditLogging}
              onChange={(e) => setSecuritySettings({...securitySettings, auditLogging: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
          </label>
        </div>
      </div>

      {/* Security Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🔐</div>
          <div className="text-sm text-gray-300">
            <p className="font-medium text-blue-400 mb-2">Recommandations de Sécurité</p>
            <ul className="space-y-1 text-gray-400 text-xs">
              <li>• Changez votre mot de passe régulièrement (au moins tous les 3 mois)</li>
              <li>• Activez l'authentification à deux facteurs pour une sécurité maximale</li>
              <li>• Utilisez la whitelist IP si vous accédez depuis des IP fixes</li>
              <li>• Gardez l'audit logging activé pour traçabilité complète</li>
              <li>• Vérifiez les logs de connexion régulièrement</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Logout Button */}
      <div className="bg-gradient-to-r from-red-900/20 to-transparent rounded-xl border-2 border-red-500/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogOut className="w-6 h-6 text-red-400" />
            <div>
              <h3 className="text-xl font-bold text-white">Déconnexion</h3>
              <p className="text-sm text-gray-400">Terminer votre session SYLINK</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-red-500/20 text-red-400 border-2 border-red-500/50 rounded-lg hover:bg-red-500/30 hover:border-red-500 transition-all font-semibold flex items-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
